// Content Preview - displays submitted content with media and caption

import { useState } from "react";
import { Button } from "@repo/ui";
import { ChevronLeft, ChevronRight, Play, Image } from "lucide-react";
import { cn } from "@repo/ui/lib/utils";

// ============================================================================
// Types
// ============================================================================

export interface ContentMedia {
  /** URL of the media (image or video) */
  url: string;
  /** Type: 'image' or 'video' */
  type: "image" | "video";
  /** Optional thumbnail URL for videos */
  thumbnailUrl?: string | null;
}

interface ContentPreviewProps {
  /** Array of media items to display */
  media: ContentMedia[];
  /** Caption text for the content */
  caption?: string | null;
  /** Optional className for the container */
  className?: string;
  /** Whether to show in a compact layout */
  compact?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ContentPreview({
  media,
  caption,
  className,
  compact = false,
}: ContentPreviewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasMultipleMedia = media.length > 1;
  const currentMedia = media[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  if (media.length === 0 && !caption) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex gap-4",
        compact ? "flex-col" : "flex-col sm:flex-row",
        className,
      )}
    >
      {/* Media carousel */}
      {media.length > 0 && (
        <div className="relative shrink-0">
          <div
            className={cn(
              "relative overflow-hidden rounded-lg bg-muted",
              compact
                ? "w-full max-w-[200px] aspect-square"
                : "w-full sm:max-w-md max-h-[500px]",
            )}
          >
            {currentMedia?.type === "video" ? (
              <video
                src={currentMedia.url}
                poster={currentMedia.thumbnailUrl ?? undefined}
                controls
                className={
                  compact
                    ? "w-full h-full object-cover"
                    : "w-full h-full object-contain"
                }
              />
            ) : currentMedia?.url ? (
              <img
                src={currentMedia.url}
                alt="Content preview"
                className={
                  compact
                    ? "w-full h-full object-cover"
                    : "w-full h-full object-contain"
                }
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.nextElementSibling?.classList.remove("hidden");
                }}
              />
            ) : null}

            {/* Fallback icon when image fails to load */}
            <div className="hidden absolute inset-0 flex items-center justify-center">
              <Image className="h-12 w-12 text-muted-foreground/50" />
            </div>

            {/* Video indicator overlay (compact/thumbnail only) */}
            {compact &&
              currentMedia?.type === "video" &&
              !currentMedia.url.includes("blob:") && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="p-3 rounded-full bg-black/50">
                    <Play className="h-6 w-6 text-white fill-white" />
                  </div>
                </div>
              )}
          </div>

          {/* Carousel navigation */}
          {hasMultipleMedia && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-1 top-1/2 -translate-y-1/2 h-8 w-8 bg-black/30 hover:bg-black/50 text-white"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 bg-black/30 hover:bg-black/50 text-white"
                onClick={goToNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Dots indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {media.map((item, index) => (
                  <button
                    key={item.url || index}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "w-2 h-2 rounded-full transition-colors",
                      index === currentIndex
                        ? "bg-white"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Caption */}
      {caption && (
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-sm font-medium text-muted-foreground">Caption:</p>
          <p className="text-sm whitespace-pre-wrap break-words">{caption}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract media items from a content requirement.
 * Handles the various media structures in the API response.
 */
export function extractContentMedia(
  content: {
    contentUrls?: (string | null)[] | null;
    previewImg?: string | null;
    content?: {
      link?: string | null;
      mediaType?: string | null;
      media?: Record<string, unknown>[] | null;
      savedMediaList?:
        | {
            url?: string | null;
            mediaType?: string | null;
            contentType?: string | null;
            screenshotUrl?: string | null;
          }[]
        | null;
    } | null;
    post?: {
      image?: string | null;
      video?: string | null;
    } | null;
    originalContent?:
      | {
          url?: string | null;
          mediaType?: string | null;
        }[]
      | null;
  } | null,
): ContentMedia[] {
  if (!content) return [];

  const mediaItems: ContentMedia[] = [];

  // Try originalContent array first (most detailed)
  if (content.originalContent && content.originalContent.length > 0) {
    for (const item of content.originalContent) {
      if (item.url) {
        mediaItems.push({
          url: item.url,
          type: isVideoUrl(item.url, item.mediaType) ? "video" : "image",
        });
      }
    }
    if (mediaItems.length > 0) return mediaItems;
  }

  // Try content.savedMediaList (real API uses this field with explicit mediaType)
  if (
    content.content?.savedMediaList &&
    content.content.savedMediaList.length > 0
  ) {
    for (const item of content.content.savedMediaList) {
      if (item.url) {
        const hint = item.mediaType ?? item.contentType ?? "";
        mediaItems.push({
          url: item.url,
          type: isVideoUrl(item.url, hint) ? "video" : "image",
          thumbnailUrl: item.screenshotUrl,
        });
      }
    }
    if (mediaItems.length > 0) return mediaItems;
  }

  // Try content.media array (has mediaType from the API)
  if (content.content?.media && content.content.media.length > 0) {
    for (const item of content.content.media) {
      const url = typeof item.url === "string" ? item.url : null;
      if (url) {
        const mediaType =
          typeof item.mediaType === "string" ? item.mediaType : "";
        mediaItems.push({
          url,
          type: isVideoUrl(url, mediaType) ? "video" : "image",
        });
      }
    }
    if (mediaItems.length > 0) return mediaItems;
  }

  // Try contentUrls array
  if (content.contentUrls && content.contentUrls.length > 0) {
    for (const url of content.contentUrls) {
      if (url) {
        mediaItems.push({
          url,
          type: isVideoUrl(url) ? "video" : "image",
        });
      }
    }
    if (mediaItems.length > 0) return mediaItems;
  }

  // Try content.link
  if (content.content?.link) {
    mediaItems.push({
      url: content.content.link,
      type: isVideoUrl(content.content.link, content.content.mediaType)
        ? "video"
        : "image",
    });
    return mediaItems;
  }

  // Try post.image or post.video
  if (content.post?.video) {
    mediaItems.push({
      url: content.post.video,
      type: "video",
    });
  } else if (content.post?.image) {
    mediaItems.push({
      url: content.post.image,
      type: "image",
    });
  }

  // Try previewImg as last resort
  if (mediaItems.length === 0 && content.previewImg) {
    mediaItems.push({
      url: content.previewImg,
      type: "image",
    });
  }

  return mediaItems;
}

/**
 * Determine if a URL points to a video based on mediaType hint or file extension.
 */
function isVideoUrl(url: string, mediaType?: string | null): boolean {
  if (mediaType && mediaType.toLowerCase().includes("video")) return true;
  return /\.(mp4|mov|webm|avi|mkv|m4v)/i.test(url);
}
